<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import {
  getAdminWechatMerchantSettings,
  saveAdminWechatMerchantSettings,
  type AdminSecretField,
  type AdminWechatMerchantSettings
} from "../admin-api";

const loading = ref(false);
const saving = ref(false);
const secretPreview = reactive({
  apiV3Key: "",
  privateKey: "",
  publicKey: ""
});
const form = reactive({
  paused: false,
  merchantId: "",
  appId: "",
  apiV3Key: "",
  certificateSerialNo: "",
  publicKeyId: ""
});
const privateKeyFile = ref<File | null>(null);
const publicKeyFile = ref<File | null>(null);
const privateKeyInput = ref<HTMLInputElement | null>(null);
const publicKeyInput = ref<HTMLInputElement | null>(null);

onMounted(loadSettings);

function onPrivateKeyFile(event: Event) {
  privateKeyFile.value = (event.target as HTMLInputElement).files?.[0] ?? null;
}

function onPublicKeyFile(event: Event) {
  publicKeyFile.value = (event.target as HTMLInputElement).files?.[0] ?? null;
}

async function loadSettings() {
  loading.value = true;
  try {
    applySettings(await getAdminWechatMerchantSettings());
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "微信商户号配置加载失败。");
  } finally {
    loading.value = false;
  }
}

async function submitSettings() {
  saving.value = true;
  try {
    const response = await saveAdminWechatMerchantSettings({
      paused: form.paused,
      merchantId: form.merchantId,
      appId: form.appId,
      apiV3Key: form.apiV3Key || undefined,
      certificateSerialNo: form.certificateSerialNo,
      publicKeyId: form.publicKeyId,
      privateKeyFile: privateKeyFile.value,
      publicKeyFile: publicKeyFile.value
    });
    message.success(response.message);
    form.apiV3Key = "";
    privateKeyFile.value = null;
    publicKeyFile.value = null;
    if (privateKeyInput.value) {
      privateKeyInput.value.value = "";
    }
    if (publicKeyInput.value) {
      publicKeyInput.value.value = "";
    }
    await loadSettings();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "微信商户号配置保存失败。");
  } finally {
    saving.value = false;
  }
}

function previewOf(field: AdminSecretField | undefined): string {
  return field?.hasValue ? field.preview : "未配置";
}

function applySettings(settings: AdminWechatMerchantSettings) {
  form.paused = settings.paused ?? false;
  form.merchantId = settings.merchantId ?? "";
  form.appId = settings.appId ?? "";
  form.certificateSerialNo = settings.certificateSerialNo ?? "";
  form.publicKeyId = settings.publicKeyId ?? "";
  form.apiV3Key = "";
  secretPreview.apiV3Key = previewOf(settings.apiV3Key);
  secretPreview.privateKey = previewOf(settings.privateKey);
  secretPreview.publicKey = previewOf(settings.publicKey);
}
</script>

<template>
  <a-card :loading="loading">
    <template #title>微信商户号配置</template>
    <template #extra>
      <a-tag :color="form.paused ? 'red' : 'green'">{{ form.paused ? "收款暂停" : "准备收款" }}</a-tag>
    </template>

    <a-alert
      class="admin-form-alert"
      type="info"
      show-icon
      message="支付回调固定为后端接口 /api/payments/wechat/notify，请在微信商户平台配置到正式域名下。"
    />

    <a-form layout="vertical" :model="form" @finish="submitSettings">
      <a-form-item>
        <a-checkbox v-model:checked="form.paused">暂停收款，保留现有密钥和证书配置</a-checkbox>
      </a-form-item>
      <a-row :gutter="16">
        <a-col :xs="24" :md="12"><a-form-item label="微信商户号"><a-input v-model:value="form.merchantId" /></a-form-item></a-col>
        <a-col :xs="24" :md="12"><a-form-item label="AppID"><a-input v-model:value="form.appId" /></a-form-item></a-col>
        <a-col :xs="24" :md="12"><a-form-item label="商户 API 证书序列号"><a-input v-model:value="form.certificateSerialNo" /></a-form-item></a-col>
        <a-col :xs="24" :md="12"><a-form-item label="微信支付公钥 ID"><a-input v-model:value="form.publicKeyId" /></a-form-item></a-col>
      </a-row>
      <a-form-item label="API v3 密钥">
        <a-input-password v-model:value="form.apiV3Key" :placeholder="`留空不修改，当前：${secretPreview.apiV3Key}`" />
      </a-form-item>
      <a-row :gutter="16">
        <a-col :xs="24" :md="12">
          <a-form-item label="商户私钥 PEM 文件">
            <input ref="privateKeyInput" type="file" accept=".pem,.key,.txt" @change="onPrivateKeyFile" />
            <div class="admin-table-subtext">当前：{{ secretPreview.privateKey }}</div>
          </a-form-item>
        </a-col>
        <a-col :xs="24" :md="12">
          <a-form-item label="微信支付公钥 PEM 文件">
            <input ref="publicKeyInput" type="file" accept=".pem,.key,.txt" @change="onPublicKeyFile" />
            <div class="admin-table-subtext">当前：{{ secretPreview.publicKey }}</div>
          </a-form-item>
        </a-col>
      </a-row>
      <a-button type="primary" html-type="submit" :loading="saving">保存微信商户配置</a-button>
    </a-form>
  </a-card>
</template>
